/**
 * Picking one currency from a list of ISO 4217 codes, by code or by name:
 * "CZK — Czech koruna", in the interface language.
 *
 * **Controlled and fetch-free**, like every picker here: the codes come in as
 * `options`, in the order to offer them — a caller puts the ones in use first.
 * Names come from the browser's `Intl.DisplayNames`, so there is no table of
 * them to translate; a code the browser cannot name shows as the bare code.
 *
 * `clearable` lets the field be emptied to null — for a setting where "none"
 * is an answer, like a display currency. The current value is always offered,
 * even when it is not in `options`, so a form opens showing what is stored.
 */
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { currentLanguage } from '../i18n';

export interface CurrencyPickerProps {
  /** ISO 4217 codes, in the order to offer them. */
  options: readonly string[];
  value: string | null;
  onChange: (code: string | null) => void;
  /** Default: "Currency". */
  label?: string;
  helperText?: string;
  placeholder?: string;
  /** The field can be emptied, and `onChange` raises null. */
  clearable?: boolean;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  fullWidth?: boolean;
}

/** "CZK — Czech koruna", or "CZK" where the browser has no name for it. */
function currencyLabel(code: string, names: Intl.DisplayNames | null): string {
  const name = names?.of(code);
  return name && name !== code ? `${code} — ${name}` : code;
}

function displayNames(): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames([currentLanguage()], { type: 'currency' });
  } catch {
    return null;
  }
}

export function CurrencyPicker({
  options,
  value,
  onChange,
  label,
  helperText,
  placeholder,
  clearable = false,
  loading = false,
  disabled = false,
  error = false,
  required = false,
  fullWidth = true,
}: CurrencyPickerProps) {
  // The subscription re-renders the names when the language changes.
  const { t } = useTranslation('common');
  const names = displayNames();
  const offered = value !== null && !options.includes(value) ? [value, ...options] : [...options];

  return (
    <Autocomplete<string, false, false, false>
      options={offered}
      value={value}
      disabled={disabled}
      loading={loading}
      // Typed `false` so `value` stays `string | null` in both modes. With
      // the clear button gone, null only reaches `onChange` when clearable.
      disableClearable={!clearable as false}
      autoHighlight
      getOptionLabel={(code) => currencyLabel(code, names)}
      onChange={(_event, code) => {
        if (code !== null || clearable) onChange(code);
      }}
      noOptionsText={t('currency.noMatches')}
      fullWidth={fullWidth}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('currency.label')}
          {...(placeholder ? { placeholder } : {})}
          {...(helperText ? { helperText } : {})}
          error={error}
          required={required}
        />
      )}
    />
  );
}
